import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from './useAccount';

const EMPTY_SUBSCRIPTION_INFO = {
  status: null,
  endsOn: null,
  trial: null,
  trialDays: null,
  planName: null,
  isExpired: false,
  daysLeft: null,
};

/**
 * 订阅状态管理 Composable
 * 提供订阅过期检查和相关信息的统一访问方法
 */
export function useSubscription() {
  const store = useStore();
  const { accountId } = useAccount();

  /**
   * 获取当前账户信息
   */
  const account = computed(() =>
    store.getters['accounts/getAccount'](accountId.value)
  );

  /**
   * 获取订阅详细信息
   */
  const subscriptionInfo = computed(() => {
    if (!account.value?.custom_attributes) {
      return EMPTY_SUBSCRIPTION_INFO;
    }

    // 订阅信息可能在两个位置：
    // 1. custom_attributes.subscription（新格式，嵌套对象）
    // 2. custom_attributes 直接包含订阅字段（旧格式，兼容）
    const subscription =
      account.value.custom_attributes.subscription ||
      account.value.custom_attributes;

    const {
      subscription_status: status,
      subscription_ends_on: endsOn,
      trial: isTrial,
      trial_days: trialDays,
      plan_name: planName,
    } = subscription;

    // 如果没有到期时间，返回空信息
    if (!endsOn) {
      return EMPTY_SUBSCRIPTION_INFO;
    }

    const endDate = new Date(endsOn);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    const isExpired = daysLeft < 0;

    return {
      status,
      endsOn: endDate,
      isTrial,
      trialDays,
      planName,
      daysLeft: isExpired ? 0 : daysLeft,
      isExpired,
    };
  });

  /**
   * 检查订阅是否已过期
   */
  const isExpired = computed(() => subscriptionInfo.value.isExpired);

  /**
   * 检查是否在白名单路由中（允许访问的路由）
   * @param {Object} route - Vue Router route 对象
   * @returns {boolean}
   */
  const isAllowedRoute = route => {
    const allowedRoutes = [
      'billing_settings_index', // 账单设置
      'subscription_plan', // 订阅套餐/续订
      'subscription_usage', // 订阅使用详情
      'subscription_invoices', // 订阅发票
      'settings_profile', // 个人资料
    ];

    return allowedRoutes.includes(route?.name);
  };

  return {
    isExpired,
    subscriptionInfo,
    isAllowedRoute,
  };
}
